import { useInfiniteQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import ProductsService, {
  ProductsQueryFilterDto,
  SemanticSearchFilters,
} from '@/services/ProductsService';

export const GET_PRODUCTS_QUERY_KEY = (
  queryFilterDto?: Partial<ProductsQueryFilterDto>,
  semanticFilters?: SemanticSearchFilters,
) => ['products', { ...queryFilterDto, semanticFilters }];

export function useGetProducts(
  queryFilterDto?: Partial<ProductsQueryFilterDto>,
  semanticFilters?: SemanticSearchFilters,
) {
  const { user } = useAuth();
  const singleSkuVision = user?.customer?.singleSkuVision;

  const hasSemanticFilters =
    (semanticFilters?.categories?.length ?? 0) > 0 ||
    Object.values(semanticFilters?.features ?? {}).some(
      (values) => values.length > 0,
    );

  const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: GET_PRODUCTS_QUERY_KEY(queryFilterDto, semanticFilters),
      initialPageParam: 1,
      queryFn: ({ pageParam }) => {
        const params = {
          ...queryFilterDto,
          page: pageParam,
          limit: queryFilterDto?.limit ?? 12,
          // When the customer runs single-SKU vision, always dedupe products
          // by SKU. Otherwise the caller decides whether to pass `unique`.
          ...(singleSkuVision && { unique: true }),
        };

        if (params.searchMode === 'semanticSearch' && params.productName) {
          return ProductsService.searchSemanticProducts(
            params,
            semanticFilters,
          );
        }

        if (params.searchMode === 'semanticSearch' && hasSemanticFilters) {
          return ProductsService.browseSemanticProducts(
            params,
            semanticFilters,
          );
        }

        return ProductsService.getProducts(params);
      },
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const data = lastPage;

        if (data.currentPage === data.totalPages || data.totalPages === 0) {
          return null;
        }

        return lastPageParam + 1;
      },
    });

  const products =
    data?.pages
      .flatMap((page) => page?.items ?? [])
      .filter(
        (
          product,
        ): product is NonNullable<
          (typeof data.pages)[number]['items'][number]
        > => Boolean(product),
      ) ?? [];

  return {
    products,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
}
